// 관리자 콘솔 공용 셸.
// 상단바 + 좌측 사이드 네비 + Outlet. 사이드 네비 항목은 도메인 단위로 그룹화한다.
// 디자인: 사용자 앱과 동일한 브랜드/잉크 토큰. 활성 메뉴는 브랜드색, 아이콘은 lucide(이모지 금지 규칙 준수).
// 반응형(개선 4·5):
//  - 데스크톱: 사이드바를 접을 수 있다(아이콘만 표시 또는 라벨 포함). 접힘 상태는 기기에 저장해 새로고침 후 유지.
//  - 모바일: 사이드바를 숨기고 상단 햄버거로 드로어(오프캔버스)를 연다. 메뉴 선택/배경 탭 시 닫힘.

import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  ClipboardList,
  LineChart,
  Ruler,
  Settings,
  Tags,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAdminAuthStore } from "@/stores/adminAuthStore.ts";
import { logoutAdmin } from "@/features/auth/authApi.ts";
import { Button } from "@oo/ui";
import { NewOrdersProvider, useNewOrders } from "@/features/orders/NewOrdersProvider.tsx";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

// 메뉴 정의. Phase 진행에 따라 항목 추가. 각 항목은 의미가 드러나는 lucide 아이콘을 동반한다.
const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "대시보드", icon: LayoutDashboard },
  { to: "/orders", label: "주문", icon: ClipboardList },
  { to: "/sales", label: "매출 관리", icon: LineChart },
  { to: "/members", label: "회원", icon: Users },
  { to: "/notifications", label: "공지", icon: Bell },
  { to: "/catalog/categories", label: "카테고리", icon: Tags },
  { to: "/catalog/products", label: "상품", icon: Package },
  { to: "/catalog/units", label: "단위", icon: Ruler },
  { to: "/catalog/banners", label: "배너", icon: ImageIcon },
  { to: "/operations/store-settings", label: "운영 설정", icon: Settings },
];

// 사이드바 접힘 설정을 기기에 저장하는 키.
const COLLAPSE_PREF_KEY = "oo-admin-sidebar-collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_PREF_KEY) === "1";
  } catch {
    return false;
  }
}

// 관리자 콘솔 메인 레이아웃 (전역 신규 주문 프로바이더로 감싼다).
// 프로바이더가 셸 바깥(위)에 있어야 내부 레이아웃에서 신규 건수 배지를 소비할 수 있다.
export default function AdminShell() {
  return (
    <NewOrdersProvider>
      <AdminShellLayout />
    </NewOrdersProvider>
  );
}

// 실제 셸 레이아웃. 신규 주문 배지를 위해 useNewOrders 를 소비한다.
function AdminShellLayout() {
  const navigate = useNavigate();
  const session = useAdminAuthStore((s) => s.session);
  const clearSession = useAdminAuthStore((s) => s.clearSession);
  // 사이드바 "주문" 메뉴 옆 신규 건수 배지용.
  const { receivedCount } = useNewOrders();

  // 데스크톱 사이드바 접힘(아이콘만) 상태 - 기기에 저장해 유지.
  const [collapsed, setCollapsed] = useState<boolean>(() => readCollapsed());
  // 모바일 드로어 열림 상태.
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_PREF_KEY, collapsed ? "1" : "0");
    } catch {
      // 저장 불가 환경은 무시.
    }
  }, [collapsed]);

  async function onLogout() {
    if (session?.refreshToken) {
      try {
        await logoutAdmin(session.refreshToken);
      } catch {
        // 네트워크 실패는 의도적 무시 - 사용자 측 세션은 어쨌든 정리
      }
    }
    clearSession();
    navigate("/login", { replace: true });
  }

  return (
    // 화면 높이를 고정하고 내부만 스크롤한다: 사이드바는 본문과 함께 스크롤되지 않고 제자리에 고정된다.
    <div className="flex h-dvh overflow-hidden bg-page text-ink-900">
      {/* 데스크톱 사이드바: 접힘 상태에 따라 너비/라벨 표시가 바뀐다. 본문과 독립적으로 고정/스크롤된다. */}
      <aside
        className={`hidden h-dvh shrink-0 flex-col overflow-y-auto border-r border-ink-200 bg-surface transition-[width] duration-fast md:flex ${
          collapsed ? "w-16" : "w-60"
        }`}
      >
        <div
          className={`flex items-center gap-2 py-4 ${collapsed ? "justify-center px-2" : "px-5"}`}
        >
          {!collapsed ? (
            <span className="text-lg font-bold text-brand-700">열린축산 관리자</span>
          ) : (
            <span className="text-lg font-bold text-brand-700">열</span>
          )}
        </div>
        <SidebarNav collapsed={collapsed} receivedCount={receivedCount} />
        {/* 접기/펼치기 토글(개선 4). */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="mt-auto flex items-center gap-2 border-t border-ink-200 px-4 py-3 text-sm text-ink-600 hover:bg-ink-100"
          title={collapsed ? "사이드바 펼치기" : "사이드바 접기"}
          aria-label={collapsed ? "사이드바 펼치기" : "사이드바 접기"}
        >
          {collapsed ? (
            <ChevronRight size={18} aria-hidden="true" />
          ) : (
            <>
              <ChevronLeft size={18} aria-hidden="true" />
              <span>접기</span>
            </>
          )}
        </button>
      </aside>

      {/* 모바일 드로어 + 배경 (md 미만에서만). */}
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-ink-900/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 left-0 flex w-64 max-w-[80%] flex-col border-r border-ink-200 bg-surface shadow-elevated">
            <div className="flex items-center justify-between px-5 py-4">
              <span className="text-lg font-bold text-brand-700">열린축산 관리자</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="rounded-md p-1 text-ink-600 hover:bg-ink-100"
                aria-label="메뉴 닫기"
              >
                <X size={20} aria-hidden="true" />
              </button>
            </div>
            {/* 메뉴 선택 시 드로어를 닫는다. */}
            <SidebarNav
              collapsed={false}
              receivedCount={receivedCount}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-ink-200 bg-surface px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            {/* 모바일 햄버거(개선 5: 모바일에서도 네비 접근 가능하게). */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="rounded-md p-1.5 text-ink-700 hover:bg-ink-100 md:hidden"
              aria-label="메뉴 열기"
            >
              <Menu size={22} aria-hidden="true" />
            </button>
            <span className="text-sm font-semibold text-brand-700 md:hidden">열린축산 관리자</span>
          </div>
          <div className="ml-auto flex items-center gap-2 text-sm sm:gap-3">
            {/* 좁은 화면에선 이름/역할을 숨겨 헤더가 넘치지 않게 한다. */}
            <span className="hidden text-ink-700 sm:inline">
              {session?.admin.displayName ?? session?.admin.loginId}
              <span className="ml-1 text-ink-600">({session?.admin.role})</span>
            </span>
            <Button variant="secondary" size="sm" onClick={onLogout}>
              <LogOut size={16} aria-hidden="true" />
              <span className="hidden sm:inline">로그아웃</span>
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// 사이드바 메뉴 목록. 데스크톱(접힘/펼침)·모바일 드로어가 공유한다.
function SidebarNav({
  collapsed,
  receivedCount,
  onNavigate,
}: {
  collapsed: boolean;
  receivedCount: number;
  onNavigate?: () => void;
}) {
  return (
    <nav className="flex flex-col gap-1 px-2">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        // 주문 메뉴에는 신규(미수락) 주문 수를 빨간 배지로 노출(새로고침 없이 폴링 갱신).
        const showBadge = item.to === "/orders" && receivedCount > 0;
        const badgeText = receivedCount > 99 ? "99+" : String(receivedCount);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            onClick={onNavigate}
            title={collapsed ? item.label : undefined}
            className={({ isActive }) =>
              `relative flex items-center rounded-md py-2 text-sm font-medium transition-colors duration-fast ${
                collapsed ? "justify-center px-2" : "gap-2.5 px-3"
              } ${isActive ? "bg-brand-600 text-white" : "text-ink-700 hover:bg-ink-100"}`
            }
          >
            <Icon size={18} aria-hidden="true" />
            {!collapsed ? <span>{item.label}</span> : null}
            {showBadge ? (
              collapsed ? (
                // 접힘 상태에선 아이콘 우상단에 점 형태 배지.
                <span
                  className="absolute right-1 top-1 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger-600 px-1 text-[10px] font-bold text-white"
                  aria-label={`신규 주문 ${receivedCount}건`}
                >
                  {badgeText}
                </span>
              ) : (
                <span
                  className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-danger-600 px-1.5 text-xs font-bold text-white"
                  aria-label={`신규 주문 ${receivedCount}건`}
                >
                  {badgeText}
                </span>
              )
            ) : null}
          </NavLink>
        );
      })}
    </nav>
  );
}
